<script setup lang="ts">
import { Head } from '@inertiajs/vue3'
import { computed } from 'vue'
import PublicInvoiceLayout from '@/Layouts/PublicInvoiceLayout.vue'
import Icon from '@/Components/Utils/Icon.vue'
import CopyButton from '@/Components/Utils/CopyButton.vue'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import { useFormatTime } from '@/Composables/useFormatTime'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faFilePdf } from '@fal'
import { library } from '@fortawesome/fontawesome-svg-core'

library.add(faFilePdf)

defineOptions({ layout: PublicInvoiceLayout })

const props = defineProps<{
    title?: string
    data: any
    pdfUrl: string
    invoiceSetting: {
        company_name: string | null
        company_address: string | null
        company_phone: string | null
        terms: string[]
        bank_accounts: { bank_name: string, account_number: string, account_name: string }[]
    }
}>()

const companyName = computed(() => props.invoiceSetting.company_name || 'Eatpack.ku')

const steps = [
    { key: 'antri_desain', label: 'Antri desain' },
    { key: 'antri_cetak', label: 'Antri cetak' },
    { key: 'pengiriman', label: 'Dikirim' },
    { key: 'selesai', label: 'Selesai' },
]

const currentStepIndex = computed(() => steps.findIndex(step => step.key === props.data.state))
</script>

<template>
    <Head :title="title" />

    <div class="mb-3 flex justify-end">
        <a :href="pdfUrl" download
            class="inline-flex items-center gap-x-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50">
            <FontAwesomeIcon icon="fal fa-file-pdf" fixed-width aria-hidden="true" />
            Download PDF
        </a>
    </div>

    <div class="overflow-hidden rounded-2xl bg-white shadow-sm">
        <div class="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 p-6">
            <div class="flex items-center gap-x-4">
                <img src="/logo-eatpackku.webp" :alt="companyName" class="h-14 w-14 shrink-0 rounded-full" />
                <div>
                    <div class="text-lg font-bold text-gray-800">{{ companyName }}</div>
                    <div v-if="invoiceSetting.company_address" class="max-w-sm text-sm text-gray-500">
                        {{ invoiceSetting.company_address }}
                    </div>
                    <div v-if="invoiceSetting.company_phone" class="text-sm text-gray-500">
                        {{ invoiceSetting.company_phone }}
                    </div>
                </div>
            </div>
            <div class="text-2xl font-bold uppercase tracking-widest text-gray-300">Invoice</div>
        </div>

        <div class="border-b border-gray-100 bg-gray-50 p-6">
            <div class="mb-4 flex items-center gap-x-2">
                <Icon v-if="data.state_icon" :data="data.state_icon" />
                <span class="font-semibold">Status: {{ data.public_state_label }}</span>
            </div>

            <ol class="grid grid-cols-4 gap-2 text-center text-xs">
                <li v-for="(step, index) in steps" :key="step.key">
                    <div
                        class="mb-1 h-1.5 rounded-full"
                        :class="index <= currentStepIndex ? 'bg-emerald-500' : 'bg-gray-200'"
                    />
                    <span :class="index <= currentStepIndex ? 'font-medium text-gray-700' : 'text-gray-400'">
                        {{ step.label }}
                    </span>
                </li>
            </ol>
        </div>

        <div class="grid gap-4 border-b border-gray-100 p-6 text-sm md:grid-cols-2">
            <div>
                <div class="mb-1 text-xs uppercase tracking-wide text-gray-400">Ditagihkan ke</div>
                <div class="font-semibold">{{ data.brand.name }}</div>
                <div class="text-gray-500">{{ data.brand.customer_name }}</div>
                <div v-if="data.brand.phone" class="text-gray-500">{{ data.brand.phone }}</div>
                <div v-if="data.brand.address" class="text-gray-500">{{ data.brand.address }}</div>
            </div>
            <dl class="flex flex-col gap-y-2 md:items-end md:text-right">
                <div>
                    <dt class="text-xs uppercase tracking-wide text-gray-400">No. invoice</dt>
                    <dd class="font-mono font-semibold">{{ data.reference }}</dd>
                </div>
                <div>
                    <dt class="text-xs uppercase tracking-wide text-gray-400">Tanggal</dt>
                    <dd class="text-gray-700">{{ useFormatTime(data.invoice_date) }}</dd>
                </div>
            </dl>
        </div>

        <div class="overflow-x-auto">
            <table class="w-full text-sm">
                <thead class="bg-gray-50 text-xs uppercase text-gray-400">
                    <tr>
                        <th class="px-6 py-3 text-left">Produk</th>
                        <th class="px-6 py-3 text-right">Qty</th>
                        <th class="px-6 py-3 text-right">Harga</th>
                        <th class="px-6 py-3 text-right">Subtotal</th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-gray-100">
                    <tr v-for="product in data.products" :key="product.id">
                        <td class="px-6 py-3">
                            <div class="font-medium">{{ product.name }}</div>
                            <div class="text-xs text-gray-400">{{ product.category_name }}</div>
                        </td>
                        <td class="px-6 py-3 text-right">{{ product.quantity }}</td>
                        <td class="px-6 py-3 text-right">{{ useFormatCurrency(product.price) }}</td>
                        <td class="px-6 py-3 text-right font-medium">{{ useFormatCurrency(product.total_amount) }}</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <dl class="divide-y divide-gray-100 border-t border-gray-100 px-6 text-sm">
            <div class="flex justify-between py-2">
                <dt class="text-gray-500">Total produk</dt>
                <dd>{{ useFormatCurrency(data.products_amount) }}</dd>
            </div>
            <div class="flex justify-between py-2">
                <dt class="text-gray-500">Ongkir</dt>
                <dd>{{ useFormatCurrency(data.shipping_cost) }}</dd>
            </div>
            <div v-if="data.shipping_subsidy" class="flex justify-between py-2">
                <dt class="text-gray-500">Subsidi ongkir</dt>
                <dd class="text-emerald-600">-{{ useFormatCurrency(data.shipping_subsidy) }}</dd>
            </div>
            <div v-if="data.additional_charge" class="flex justify-between py-2">
                <dt class="text-gray-500">
                    Biaya tambahan
                    <span v-if="data.additional_charge_note" class="text-xs">({{ data.additional_charge_note }})</span>
                </dt>
                <dd>{{ useFormatCurrency(data.additional_charge) }}</dd>
            </div>
            <div class="flex justify-between py-3 text-base">
                <dt class="font-semibold">Total tagihan</dt>
                <dd class="font-bold">{{ useFormatCurrency(data.total_amount) }}</dd>
            </div>
            <div class="flex justify-between py-2">
                <dt class="text-gray-500">Sudah dibayar</dt>
                <dd class="text-emerald-600">{{ useFormatCurrency(data.paid_amount) }}</dd>
            </div>
            <div v-if="data.canceled_at" class="py-2 text-sm text-gray-500">
                Pesanan ini dibatalkan.
                <template v-if="data.paid_amount > 0">
                    Pembayaran yang sudah masuk {{ useFormatCurrency(data.paid_amount) }} akan diurus oleh tim kami.
                </template>
            </div>
            <div v-else class="flex justify-between py-2">
                <dt class="text-gray-500">Sisa</dt>
                <dd :class="data.remaining_amount > 0 ? 'text-red-500' : 'text-emerald-600'">
                    {{ useFormatCurrency(data.remaining_amount) }}
                </dd>
            </div>
        </dl>

        <div v-if="data.payments.length" class="border-t border-gray-100 px-6 py-4">
            <div class="mb-2 text-xs uppercase tracking-wide text-gray-400">Riwayat pembayaran</div>
            <ul class="divide-y divide-gray-100 text-sm">
                <li v-for="payment in data.payments" :key="payment.id" class="flex justify-between py-2">
                    <span>
                        {{ payment.type_label }}
                        <span class="text-xs text-gray-400">{{ useFormatTime(payment.paid_at) }}</span>
                    </span>
                    <span class="font-medium">{{ useFormatCurrency(payment.amount) }}</span>
                </li>
            </ul>
        </div>

        <div v-if="invoiceSetting.bank_accounts.length || invoiceSetting.terms.length"
            class="grid gap-6 border-t border-gray-100 bg-gray-50 p-6 text-sm md:grid-cols-2">
            <div v-if="invoiceSetting.terms.length"
                :class="invoiceSetting.bank_accounts.length ? '' : 'md:col-span-2'">
                <div class="mb-2 text-xs uppercase tracking-wide text-gray-400">Syarat & ketentuan</div>
                <ol class="flex list-decimal flex-col gap-1 pl-4 text-gray-600">
                    <li v-for="(term, index) in invoiceSetting.terms" :key="`term-${index}`">{{ term }}</li>
                </ol>
            </div>
            
            <div v-if="invoiceSetting.bank_accounts.length">
                <div class="mb-2 text-xs uppercase tracking-wide text-gray-400">Metode pembayaran</div>
                <ul class="flex flex-col gap-2">
                    <li v-for="(account, index) in invoiceSetting.bank_accounts" :key="`bank-account-${index}`"
                        class="rounded-lg border border-gray-200 bg-white px-4 py-3">
                        <div class="text-xs font-semibold uppercase text-gray-500">{{ account.bank_name }}</div>
                        <div class="flex items-center gap-x-2">
                            <span class="font-mono text-base font-semibold tracking-wide text-gray-800">
                                {{ account.account_number }}
                            </span>
                            <CopyButton :text="account.account_number" class="text-sm text-gray-600" />
                        </div>
                        <div class="text-gray-500">a.n {{ account.account_name }}</div>
                    </li>
                </ul>
            </div>
        </div>
    </div>

    <p class="mt-4 text-center text-xs text-gray-400">
        Halaman ini otomatis diperbarui mengikuti status pesanan Anda.
    </p>
</template>
